import moment from 'moment';
import { getTicketModifications, ticketCalc, calculateTotalDiff } from './helpers';

export const updateToSimpleText = (updateData, initialValues, values) => {
    let text = '';

    // Add activity context at the top
    const activityName = initialValues?.activity?.name || 'Unknown Activity';
    // original_activity_start is the current effective start time (the
    // linked custom booking's own time once one is set — see
    // BookingItemEditPage.js), not the raw, possibly-stale activity_start.
    const activityDate = initialValues?.original_activity_start
        ? moment(initialValues.original_activity_start).format('ddd MMM DD, YYYY')
        : 'Unknown Date';
    text += `Changes for ${activityName} on ${activityDate}:\n\n`;

    // Calculate and show financial summary first
    const totalDiff = calculateTotalDiff(values);

    if (totalDiff !== 0) {
        const action = totalDiff > 0 ? 'Additional charge' : 'Refund amount';
        const amount = Math.abs(totalDiff);
        text += `${action}: $${amount.toFixed(2)}\n\n`;
    }

    // Show changes
    if (updateData.activity_start) {
        text += `- Update activity start time to ${moment(updateData.activity_start).format(
            'ddd MMM DD, YYYY [at] h:mm A'
        )}\n`;
    }
    if (updateData.booking_info?.confirmation_code) {
        text += `- Update confirmation code to "${updateData.booking_info.confirmation_code}"\n`;
    }
    if (updateData.booking_info?.vendor_message) {
        text += `- Update vendor message to "${updateData.booking_info.vendor_message}"\n`;
    }
    if (typeof updateData.re_source_booking !== 'undefined') {
        if (updateData.re_source_booking === 0) {
            text += `- Un-assign booking agent\n`;
        } else {
            text += `- Re-assign booking to agent ID ${updateData.re_source_booking}\n`;
        }
    }
    if (updateData.modify_tickets || updateData.add_tickets) {
        text += `- Modify ticket quantities:\n`;
        values?.uniqueTickets?.forEach(u => {
            const { add, modify } = getTicketModifications(u);
            if (add.length > 0) {
                text += `   - Add ${u.quantity - u.existingTickets.length} x "${u.name}"\n`;
            }
            if (modify.length > 0) {
                // this is a remove
                text += `   - Remove ${u.existingTickets.length - u.quantity} x "${u.name}"\n`;
            }
        });
    }
    if (updateData.agentnote) {
        const oldNotes = initialValues.agentnote?.agentnotes || [];
        const newNotes = values.agentnote?.agentnotes || [];

        // Check for added notes
        newNotes.forEach(note => {
            const existed = oldNotes.find(n => n.id === note.id);
            if (!existed) {
                text += `- Add agent note: "${note.note || ''}"\n`;
            } else if (existed.note !== note.note) {
                text += `- Update agent note: "${note.note || ''}"\n`;
            }
        });

        // Check for deleted notes
        oldNotes.forEach(note => {
            const stillExists = newNotes.find(n => n.id === note.id);
            if (!stillExists) {
                text += `- Delete agent note: "${note.note || ''}"\n`;
            }
        });
    }
    if (updateData.notification) {
        text += `- Update notification preferences\n`;
    }
    if (updateData.custom_booking_id) {
        text += `- Link to custom booking ID ${updateData.custom_booking_id}\n`;
    }
    if (updateData.force_payment_method && updateData.force_payment_method !== 'none') {
        text += `- Set payment method to "${updateData.force_payment_method}"\n`;
    }
    if (updateData.force_payment_method_note) {
        text += `- Add payment note\n`;
    }
    if (updateData.delete_discounts && updateData.delete_discounts.length > 0) {
        text += `- Remove ${updateData.delete_discounts.length} discount(s)\n`;
    }

    return text.trim();
};

export const buildUpdateData = ({ initialValues, values, customer }) => {
    // we just send in the "diff", so we can build extra logic around it
    const updateData = {};

    // Activity start time changes
    if (initialValues.activity_start !== values.activity_start) {
        updateData.activity_start = values.activity_start;
        if (values.activity_end && initialValues.activity_end !== values.activity_end) {
            updateData.activity_end = values.activity_end;
        }
    }

    // Booking info (includes the confirmation code + vendor message)
    if (JSON.stringify(initialValues.booking_info) !== JSON.stringify(values.booking_info)) {
        updateData.booking_info = values.booking_info;
    }

    // Agent note handling (object with agentnotes array inside)
    if (JSON.stringify(initialValues.agentnote) !== JSON.stringify(values.agentnote)) {
        updateData.agentnote = values.agentnote;
    }

    // Notification settings (global)
    if (
        JSON.stringify(initialValues.booking.notification) !==
        JSON.stringify(values.booking.notification)
    ) {
        updateData.notification = values.booking.notification;
    }

    // Re-source booking
    if (initialValues.booking.agent_id !== values.booking.agent_id) {
        updateData.re_source_booking = values.booking.agent_id;
    }

    const modify_tickets = [];
    const add_tickets = [];

    for (let i = 0; i < values?.uniqueTickets?.length; i++) {
        const u = values.uniqueTickets[i];
        const { add, modify } = getTicketModifications(u);
        modify_tickets.push(...modify);
        add_tickets.push(...add);
    }

    if (modify_tickets.length > 0) {
        updateData.modify_tickets = modify_tickets;
    }
    if (add_tickets.length > 0) {
        updateData.add_tickets = add_tickets;
    }

    // Custom booking changes
    if (
        initialValues.custom_booking_id !== values.custom_booking_id ||
        JSON.stringify(initialValues.custom_booking) !== JSON.stringify(values.custom_booking)
    ) {
        updateData.custom_booking_id = values.custom_booking_id;
        if (values.custom_booking_id !== null) {
            updateData.custom_booking = {
                activity_start: values.custom_booking?.activity_start,
                activity_end: values.custom_booking?.activity_end,
                voucher_number: values.custom_booking?.voucher_number,
                vendor_code: values.custom_booking?.vendor_code
            };
        }
    }

    // force into method
    updateData.force_payment_method =
        values.force_payment_method === 'none' ? null : values.force_payment_method;
    updateData.force_payment_method_note = values.force_payment_method_note;
    if (values.force_payment_method_payment_intent) {
        updateData.force_payment_method_payment_intent = values.force_payment_method_payment_intent;
    }

    const finalDiff = calculateTotalDiff(values);

    if (finalDiff > 0) {
        updateData.payment_override = {
            method: updateData.force_payment_method,
            note: values.force_payment_method_note || '',
            payment_intent: values.force_payment_method_payment_intent || null,
            stripe_token: customer?.stripe?.id || null,
            stripe_last4: customer?.stripe_last4 || null
        };
    }

    // delete discount list
    if (values.delete_discounts && values.delete_discounts.length > 0) {
        updateData.delete_discounts = values.delete_discounts;
    }

    // remove force_payment_method if null AND force_payment_method_note is empty AND no other changes
    if (
        updateData.force_payment_method === null &&
        (updateData.force_payment_method_note === '' || !updateData.force_payment_method_note) &&
        !updateData.payment_override &&
        Object.keys(updateData).length === 2 // only force_payment_method, force_payment_method_note
    ) {
        delete updateData.force_payment_method;
        delete updateData.force_payment_method_note;
    }

    return updateData;
};
